import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { SITE } from "../lib/site";

const NAV = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services" },
    { to: "/projects", label: "Projects" },
    { to: "/about", label: "About" },
    { to: "/faq", label: "FAQ" },
    { to: "/contact", label: "Contact" },
];

export default function Header() {
    const [open, setOpen] = useState(false);

    return (
        <header
            className="bg-background/80 backdrop-blur-xl border-b border-border/60 sticky top-0 z-50"
            data-testid="site-header"
        >
            <div className="container-x flex items-center justify-between h-20">
                <Link
                    to="/"
                    className="flex items-center gap-3 group"
                    data-testid="logo-link"
                >
                    <img
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/dandy-logo.png`}
                        alt="Dandy Builders & Property Care Ltd"
                        className="h-12 w-12 rounded-md object-contain bg-black p-1 shadow-sm"
                    />
                    <div className="leading-tight">
                        <div className="font-heading font-bold text-foreground tracking-tight">
                            {SITE.name}
                        </div>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                            & Property Care
                        </div>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-8">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.to === "/"}
                            data-testid={`nav-${n.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `text-sm font-medium transition-colors ${
                                    isActive
                                        ? "text-primary"
                                        : "text-muted-foreground hover:text-primary"
                                }`
                            }
                        >
                            {n.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    <a
                        href={SITE.phoneLink}
                        className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
                        data-testid="header-phone-link"
                    >
                        <Phone size={16} strokeWidth={1.75} />
                        {SITE.phone}
                    </a>
                    <Link
                        to="/quote"
                        className="btn-primary !py-2.5 !px-5 text-sm"
                        data-testid="header-quote-btn"
                    >
                        Get a Quote
                    </Link>
                </div>

                <button
                    onClick={() => setOpen(true)}
                    className="lg:hidden p-2 text-foreground"
                    data-testid="mobile-menu-toggle"
                    aria-label="Open menu"
                >
                    <Menu size={24} />
                </button>
            </div>

            {open && (
                <div className="fixed inset-0 z-50 bg-background lg:hidden flex flex-col">
                    <div className="flex items-center justify-between h-20 container-x">
                        <span className="font-heading font-bold text-lg">
                            Menu
                        </span>
                        <button
                            onClick={() => setOpen(false)}
                            className="p-2"
                            data-testid="mobile-menu-close"
                            aria-label="Close menu"
                        >
                            <X size={24} />
                        </button>
                    </div>
                    <div className="flex flex-col gap-2 px-6 mt-4">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.to === "/"}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `text-2xl font-heading font-bold py-3 border-b border-border ${isActive ? "text-primary" : "text-foreground"}`
                                }
                            >
                                {n.label}
                            </NavLink>
                        ))}
                        <Link
                            to="/quote"
                            onClick={() => setOpen(false)}
                            className="btn-primary mt-6"
                            data-testid="mobile-quote-btn"
                        >
                            Get a Quote
                        </Link>
                        <a
                            href={SITE.phoneLink}
                            className="btn-outline mt-3"
                            data-testid="mobile-call-btn"
                        >
                            <Phone size={18} /> {SITE.phone}
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
